import { useMemo, useState } from 'react';
import type { ColDef } from 'ag-grid-community';
import type { Contact } from '../api/types';
import { DataGrid } from './DataGrid';
import { ErrorBanner, SuccessBanner } from './ui';
import { Button, CheckboxInput, NumberInput, TextInput } from '../neon/controls';
import { Modal } from '../neon/Modal';
import { DefinitionList } from './DetailSection';
import { useDomId } from '../hooks/useDomId';
import { contactName } from '../utils/format';

export interface ContactsTabProps {
  contacts: Contact[];
  loading: boolean;
  onReload: () => void;
  create: (body: Record<string, any>) => Promise<unknown>;
  update: (id: number, body: Record<string, any>) => Promise<unknown>;
  remove: (id: number) => Promise<unknown>;
}

type Draft = Partial<Contact>;

const EMPTY: Draft = { prefix: '', first_name: '', last_name: '', title: '', email: '', phone: '', job_function: null, is_active: 1 };

export function ContactsTab({ contacts, loading, onReload, create, update, remove }: ContactsTabProps) {
  const formId = useDomId('contact');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [viewing, setViewing] = useState<Contact | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const columns = useMemo<ColDef<Contact>[]>(
    () => [
      { headerName: 'Name', valueGetter: (p) => (p.data ? contactName(p.data) : ''), minWidth: 180 },
      { headerName: 'Title', field: 'title', minWidth: 140 },
      { headerName: 'Email', field: 'email', minWidth: 200 },
      { headerName: 'Phone', field: 'phone', minWidth: 130 },
      {
        headerName: 'Active',
        field: 'is_active',
        maxWidth: 110,
        valueFormatter: (p) => (Number(p.value) === 1 ? 'Yes' : 'No'),
      },
    ],
    [],
  );

  const startAdd = () => {
    setError(null);
    setMessage(null);
    setViewing(null);
    setDraft({ ...EMPTY });
  };

  const startView = (c: Contact) => {
    setError(null);
    setMessage(null);
    setViewing(c);
  };

  const startEdit = (c: Contact) => {
    setError(null);
    setMessage(null);
    setViewing(null);
    setDraft({ ...c });
  };

  const set = (patch: Draft) => setDraft((d) => ({ ...d, ...patch }));

  const doSave = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      const body = {
        prefix: draft.prefix,
        first_name: draft.first_name,
        last_name: draft.last_name,
        title: draft.title,
        email: draft.email,
        phone: draft.phone,
        job_function: draft.job_function ? Number(draft.job_function) : null,
        is_active: draft.is_active ? 1 : 0,
      };
      if (draft.id) await update(draft.id, body);
      else await create(body);
      setMessage(draft.id ? 'Contact updated.' : 'Contact created.');
      setDraft(null);
      onReload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (c: Contact) => {
    if (!window.confirm(`Delete contact “${contactName(c)}”?`)) return;
    setSaving(true);
    setError(null);
    try {
      await remove(c.id);
      setMessage('Contact deleted.');
      if (draft?.id === c.id) setDraft(null);
      onReload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tab-content">
      <div className="toolbar">
        <h3>Contacts ({contacts.length})</h3>
        <Button id="contacts-add" text="Add contact" icon="add" onClick={startAdd} />
      </div>

      <SuccessBanner message={message} />
      <ErrorBanner error={error} />

      <DataGrid<Contact>
        rows={contacts}
        columns={columns}
        loading={loading}
        getRowId={(r) => String(r.id)}
        onRowClick={startView}
        height={360}
      />

      {viewing && (
        <Modal
          title={contactName(viewing)}
          size="small"
          onClose={() => setViewing(null)}
          footer={
            <>
              <Button
                id="contact-view-edit"
                text="Edit"
                icon="edit"
                onClick={() => {
                  const c = viewing;
                  startEdit(c);
                }}
              />
              <Button id="contact-view-close" text="Close" variant="tertiary" onClick={() => setViewing(null)} />
            </>
          }
        >
          <DefinitionList
            items={[
              { label: 'Name', value: contactName(viewing) },
              { label: 'Title', value: viewing.title || '—' },
              { label: 'Email', value: viewing.email || '—' },
              { label: 'Phone', value: viewing.phone || '—' },
              { label: 'Job function', value: viewing.job_function ?? '—' },
              { label: 'Active', value: Number(viewing.is_active) === 1 ? 'Yes' : 'No' },
            ]}
          />
        </Modal>
      )}

      {draft && (
        <Modal
          title={draft.id ? `Edit contact #${draft.id}` : 'New contact'}
          size="medium"
          onClose={() => setDraft(null)}
          footer={
            <>
              <Button id={`${formId}-save`} text={saving ? 'Saving…' : 'Save contact'} onClick={() => void doSave()} loading={saving} />
              <Button id={`${formId}-cancel`} text="Cancel" variant="tertiary" onClick={() => setDraft(null)} disabled={saving} />
              {draft.id ? (
                <Button
                  id={`${formId}-delete`}
                  text="Delete"
                  variant="secondary"
                  icon="trash"
                  onClick={() => draft.id && onDelete(draft as Contact)}
                  disabled={saving}
                />
              ) : null}
            </>
          }
        >
          <ErrorBanner error={error} />
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void doSave();
            }}
          >
            <div className="form-grid">
              <TextInput id={`${formId}-prefix`} label="Prefix" value={draft.prefix ?? ''} onChange={(v) => set({ prefix: v })} />
              <TextInput id={`${formId}-first`} label="First name" value={draft.first_name ?? ''} onChange={(v) => set({ first_name: v })} />
              <TextInput id={`${formId}-last`} label="Last name" value={draft.last_name ?? ''} onChange={(v) => set({ last_name: v })} />
              <TextInput id={`${formId}-title`} label="Title" value={draft.title ?? ''} onChange={(v) => set({ title: v })} />
              <TextInput id={`${formId}-email`} label="Email" value={draft.email ?? ''} onChange={(v) => set({ email: v })} email />
              <TextInput id={`${formId}-phone`} label="Phone" value={draft.phone ?? ''} onChange={(v) => set({ phone: v })} />
              <NumberInput
                id={`${formId}-jobfn`}
                label="Job function (code)"
                value={draft.job_function ?? undefined}
                onChange={(v) => set({ job_function: v ?? null })}
                integer
              />
              <CheckboxInput
                id={`${formId}-active`}
                label="Active"
                checked={Number(draft.is_active) === 1}
                onChange={(v) => set({ is_active: v ? 1 : 0 })}
              />
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
